import React, {
  useRef,
  useState,
  useEffect,
  useCallback
} from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Button
} from '@sopranodesign/pulse-components';
import { useMutation, useQuery } from '@sopranodesign/pulse-hooks';
import {
  useBot,
  useComposer,
  useDelayedBot,
  isValidMessage,
  ConverseSendButton,
  formatClientMessage,
  ConverseBaseComposer
} from '@sopranodesign/pulse-volt';
import { FileSelect } from '@sopranodesign/pulse-files';
import {
  useAuth,
  FEATURE_GATES,
  PermissionFilter,
  PermissionsFilter,
  GlobalFeatureGateFilter
} from '@sopranodesign/pulse-auth';

import { sendMessage as sendMessageApi, getSuggestion } from '../../../../../api';
import { useUpdateTicketResponsesCache } from '../../../../../hooks';
import AgentComposerSuggestionList from './AgentComposerSuggestionList';
import AgentComposerSelectMenu from './AgentComposerSelectMenu';

const MAX_MESSAGE_LENGTH = 1000;
const MIN_MESSAGE_LIMIT = 1;
const TOTAL_SUGGESTION = 1;

const isValid = message => isValidMessage(message, { min: MIN_MESSAGE_LIMIT, max: MAX_MESSAGE_LENGTH });

const FileAttachButton = ({ onClick, disabled }) => (
  <Button
    fat
    middle
    variant="inline"
    icon="paperClip"
    colour="secondary"
    onClick={onClick}
    disabled={disabled}
    aria-label="Send file"
    title="Send file"
  />
);

FileAttachButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired
};

const AgentComposer = ({ subscriberId, ticketId }) => {
  const [showFileSelect, setShowFileSelect] = useState(false);
  const [composer, setComposer] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const { hasActiveFeatureGate } = useAuth();
  const { reply, setReply } = useComposer();
  const { setMessages } = useDelayedBot();
  const { messages } = useBot();
  const updateTicketResponsesCache = useUpdateTicketResponsesCache();

  const mutation = useMutation({
    mutationFn: sendMessageApi,
    onSuccess: ({ data }) => {
      updateTicketResponsesCache({ ticketId, responses: data });
    }
  });

  const composerQuery = useQuery({ queryKey: ['composers'] });
  const settingQuery = useQuery({
    queryKey: ['live-chat/settings'],
    enabled: hasActiveFeatureGate(FEATURE_GATES.GENERATIVE_AI)
  });

  const suggestionMutation = useMutation({
    mutationFn: async () => {
      const response = await getSuggestion(subscriberId);
      return response?.data?.data?.suggestions?.[0] || null;
    }
  });

  const showSuggestionsEnabled = settingQuery.data?.data?.has_auto_generate_responses_enabled;
  const textareaRef = useRef(null);
  const disabled = mutation.isPending;
  const lastClientMessageRef = useRef(null);

  const fetchSuggestions = useCallback(async () => {
    setIsLoadingSuggestions(true);
    setSuggestions([]);

    try {
      const calls = Array.from({ length: TOTAL_SUGGESTION }, () => suggestionMutation.mutateAsync());
      const results = await Promise.allSettled(calls);

      const validSuggestions = results
        .filter(result => result.status === 'fulfilled' && result.value)
        .map(result => result.value);

      setSuggestions(validSuggestions);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [suggestionMutation]);

  const sendMessage = useCallback(
    response => {
      const payload = {
        subscriberId,
        responses: [{ ...response, composer_id: composer }]
      };

      setMessages(prev => prev.concat(formatClientMessage(response)));
      mutation.mutate(payload);
      setComposer(null);
    },
    [subscriberId, composer, setMessages, mutation]
  );

  const handleSubmit = useCallback(() => {
    const validatedMessage = isValid(reply);
    if (!validatedMessage) {
      return;
    }

    sendMessage({ type: 'standard', content: { text: reply } });
    setReply('');
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = '';
    }
  }, [reply, sendMessage, setReply]);

  const handleFileSelect = useCallback(
    file => {
      sendMessage({
        type: 'file',
        content: { url: file.link, text: file.name }
      });
      setShowFileSelect(false);
    },
    [sendMessage]
  );

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = '';
      const newHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${newHeight}px`;
      textareaRef.current.style.overflow = newHeight > 150 ? 'auto' : 'hidden';
    }
  }, [reply, disabled]);

  useEffect(() => {
    if (!hasActiveFeatureGate(FEATURE_GATES.GENERATIVE_AI) || !showSuggestionsEnabled || !messages?.length) {
      return;
    }

    const lastClientMessage = [...messages].reverse().find(msg => msg.direction === 'toClient');
    if (!lastClientMessage) {
      return;
    }

    const uniqueKey = lastClientMessage.id || lastClientMessage.content?.text;
    if (uniqueKey !== lastClientMessageRef.current) {
      lastClientMessageRef.current = uniqueKey;
      setSuggestions([]);
      setIsLoadingSuggestions(true);
      fetchSuggestions();
    }
  }, [messages, hasActiveFeatureGate, showSuggestionsEnabled, fetchSuggestions]);

  const forceUpdateReply = useCallback(value => {
    setReply(value);
    if (textareaRef.current) {
      textareaRef.current.value = value;
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      textareaRef.current.style.overflow = textareaRef.current.scrollHeight > 150 ? 'auto' : 'hidden';
    }
  }, [setReply]);

  return (
    <>
      {showFileSelect && (
        <FileSelect handleFileSelect={handleFileSelect} handleCancel={() => setShowFileSelect(false)} />
      )}

      <ConverseBaseComposer.Container style={{ background: 'white' }}>
        <GlobalFeatureGateFilter for={FEATURE_GATES.GENERATIVE_AI}>
          {showSuggestionsEnabled && showSuggestions && (
            <AgentComposerSuggestionList
              suggestions={suggestions}
              setReply={forceUpdateReply}
              isLoading={isLoadingSuggestions}
              totalSlots={TOTAL_SUGGESTION}
            />
          )}
        </GlobalFeatureGateFilter>

        <Flex middle fat="true" center pb pt>
          <Flex ml mr fat>
            <ConverseBaseComposer.TextArea
              aria-label="Message"
              onChange={e => setReply(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              value={reply}
              placeholder="Send a message"
              disabled={disabled}
              ref={textareaRef}
              rows={1}
            />
            <span
              style={{
                position: 'absolute',
                bottom: '4px',
                right: '10px',
                fontSize: '12px',
                color: reply.length > MAX_MESSAGE_LENGTH ? '#dc2626' : '#6b7280'
              }}
              title={
                reply.length > MAX_MESSAGE_LENGTH
                  ? `The message provided is over maximum allowed ${MAX_MESSAGE_LENGTH} characters`
                  : ''
              }
            >
              {reply.length} / {MAX_MESSAGE_LENGTH} characters
            </span>
          </Flex>

          <GlobalFeatureGateFilter for={FEATURE_GATES.GENERATIVE_AI}>
            {showSuggestionsEnabled && (
              <Flex mrSm>
                <Button
                  fat
                  middle
                  variant="inline"
                  icon={showSuggestions ? 'eyeOff' : 'eye'}
                  colour="secondary"
                  title={showSuggestions ? 'Click to hide suggestions' : 'Click to show suggestions'}
                  aria-label={showSuggestions ? 'Hide suggestions' : 'Show suggestions'}
                  onClick={() => setShowSuggestions(prev => !prev)}
                />
              </Flex>
            )}
          </GlobalFeatureGateFilter>

          <PermissionFilter can="view composers">
            <Flex mrSm>
              <AgentComposerSelectMenu
                composerQuery={composerQuery}
                composer={composer}
                setComposer={setComposer}
                disabled={disabled}
              />
            </Flex>
          </PermissionFilter>

          <PermissionsFilter can={['view files', 'view file sources']}>
            <Flex mrSm>
              <FileAttachButton onClick={() => setShowFileSelect(true)} disabled={disabled} />
            </Flex>
          </PermissionsFilter>

          <Flex mr>
            <ConverseSendButton
              aria-label="Send message"
              title="Send message"
              disabled={disabled || reply.length > MAX_MESSAGE_LENGTH || reply.length < MIN_MESSAGE_LIMIT}
              onClick={handleSubmit}
            />
          </Flex>
        </Flex>
      </ConverseBaseComposer.Container>
    </>
  );
};

AgentComposer.propTypes = {
  ticketId: PropTypes.number.isRequired,
  subscriberId: PropTypes.number.isRequired
};

export default AgentComposer;